<template>
  <div class="purchase-table">
    <!-- 操作栏 -->
    <div class="table-toolbar" v-if="showToolbar">
      <div class="toolbar-left">
        <slot name="toolbarLeft"></slot>
      </div>
      <div class="toolbar-right">
        <el-button @click="handleRefresh" size="small" :loading="loading">刷新</el-button>
        <el-button @click="handleSetup" size="small">设置</el-button>
        <slot name="toolbarRight"></slot>
      </div>
    </div>

    <el-table 
      :data="tableData" 
      style="width: 100%"
      v-loading="loading"
      @expand-change="handleExpandChange"
      :header-cell-style="{ background: '#EBEBEB', color: '#606666' }"
      :height="autoHeight ? null : tableHeight"
      :max-height="autoHeight ? null : undefined"
      ref="purchaseTable"
    >
      <el-table-column type="expand" v-if="showExpand">
        <template #default="props">
          <div class="expand-content">
            <el-table 
              :data="props.row.products" 
              style="width: 100%"
              :show-header="true"
              :header-cell-style="{ background: '#fff', color: '#606666'  }"
            >
              <el-table-column type="index" label="序号" width="60" v-if="expandColumns.length > 0" />
              
              <!-- 动态渲染展开行列 -->
              <el-table-column 
                v-for="col in expandColumns" 
                :key="col.prop"
                :label="col.label"
                :width="col.width"
                :min-width="col.minWidth"
                :align="col.align || 'left'"
                :fixed="col.fixed"
              >
                <template #default="scope">
                  <slot :name="`expand-${col.prop}`" :row="scope.row" :column="col">
                    <span>{{ scope.row[col.prop] }}</span>
                  </slot>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </template>
      </el-table-column>
      <el-table-column type="index" label="序号" width="60" />
      
      <!-- 动态渲染主表格列 -->
      <template v-for="col in internalColumns" :key="col.prop">
        <el-table-column 
          v-if="!columnConfig[col.prop] || !columnConfig[col.prop].hide"
          :prop="col.prop"
          :label="col.label"
          :width="col.width"
          :min-width="col.minWidth"
          :align="col.align || 'left'"
          :fixed="col.fixed"
        >
          <template #default="scope">
            <slot :name="col.prop" :row="scope.row" :column="col">
              <span>{{ scope.row[col.prop] }}</span>
            </slot>
          </template>
        </el-table-column>
      </template>
      
      <!-- 操作列 -->
      <el-table-column 
        v-if="showOperation"
        label="操作" 
        :width="operationWidth" 
        align="center" 
        :fixed="operationFixed"
      >
        <template #default="scope">
          <slot name="operation" :row="scope.row">
            <el-button type="text" @click="handleView(scope.row)">查看</el-button>
          </slot>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination" v-if="showPagination">
      <el-pagination
        v-model:current-page="pageInfo.pageNum"
        v-model:page-size="pageInfo.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pageInfo.total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 设置对话框 -->
    <SetupDialog v-model="showSetupDialog" :data="internalColumns" @change="handleSetupChange" />
  </div>
</template>

<script>
import SetupDialog from './SetupDialog.vue';

export default {
  name: 'PurchaseTable',
  components: {
    SetupDialog
  },
  props: {
    tableData: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    pageInfo: {
      type: Object,
      default: () => ({
        pageNum: 1,
        pageSize: 20,
        total: 0
      })
    },
    columns: {
      type: Array,
      default: () => []
    },
    showExpand: {
      type: Boolean,
      default: true
    },
    expandColumns: {
      type: Array,
      default: () => []
    },
    showOperation: {
      type: Boolean,
      default: false
    },
    operationWidth: {
      type: String,
      default: '150'
    },
    operationFixed: {
      type: String,
      default: 'right'
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    autoHeight: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showSetupDialog: false,
      columnConfig: {},
      internalColumns: [],
      tableHeight: 0
    };
  },
  mounted() {
    if (!this.autoHeight) {
      this.calculateHeight();
      window.addEventListener('resize', this.calculateHeight);
    }
  },
  beforeUnmount() {
    if (!this.autoHeight) {
      window.removeEventListener('resize', this.calculateHeight);
    }
  },
  created() {
    this.initColumns();
  },
  watch: {
    columns: {
      handler() {
        this.initColumns();
      },
      deep: true
    }
  },
  methods: {
    calculateHeight() {
      this.$nextTick(() => {
        if (this.$refs.purchaseTable) {
          const tableEl = this.$refs.purchaseTable.$el;
          const top = tableEl.getBoundingClientRect().top;
          const paginationHeight = this.showPagination ? 74 : 0; // 根据是否显示分页器调整高度
          const bottomPadding = 20; // 底部padding
          this.tableHeight = window.innerHeight - top - paginationHeight - bottomPadding;
          
          // 最小高度300px
          if (this.tableHeight < 300) {
            this.tableHeight = 300;
          }
        }
      });
    },
    initColumns() {
      if (this.columns && this.columns.length > 0) {
        this.internalColumns = this.columns.map(col => ({
          ...col,
          hide: col.hide || false
        }));
        const config = {};
        this.columns.forEach(col => {
          config[col.prop] = { hide: col.hide || false };
        });
        this.columnConfig = config;
      }
    },
    handleExpandChange(row, expandedRows) {
      this.$emit('expand-change', row, expandedRows);
    },
    handleView(row) {
      this.$emit('view', row);
    },
    handleCancel(row) {
      this.$emit('cancel', row);
    },
    handleProductAction(orderRow, productRow, action) {
      this.$emit('product-action', { orderRow, productRow, action });
    },
    handleSizeChange(val) {
      this.$emit('size-change', val);
    },
    handleCurrentChange(val) {
      this.$emit('current-change', val);
    },
    handleRefresh() {
      this.$emit('refresh');
    },
    handleSetup() {
      this.showSetupDialog = true;
    },
    handleSetupChange(selectedColumns) {
      this.internalColumns.forEach(item => {
        item.hide = !selectedColumns.some(prop => prop === item.prop);
        if (this.columnConfig[item.prop]) {
          this.columnConfig[item.prop].hide = item.hide;
        }
      });
    },
    getColumnWidth(col) {
      return col.width || (col.minWidth ? undefined : '120');
    }
  }
};
</script>

<style scoped lang="less">
.purchase-table {
  .table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding:0 0 10px 0;
    
    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    
    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }

  .pagination {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
